import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import {
  Landmark,
  CheckCircle2,
  ArrowRight,
  Shield,
  FileCheck,
  Globe,
  BarChart3,
} from "lucide-react";

export default function Government() {
  const features = [
    {
      icon: Shield,
      title: "Government-Grade Security",
      description: "SOC 2 Type II certified, FISMA compliant, with FedRAMP authorization in progress.",
    },
    {
      icon: FileCheck,
      title: "Grant Reporting",
      description: "Automated reporting tools designed specifically for WIOA, TANF, and other workforce grant requirements.",
    },
    {
      icon: Globe,
      title: "Multi-Language Support",
      description: "Serve diverse populations with support for 20+ languages including Spanish, Mandarin, and Arabic.",
    },
    {
      icon: BarChart3,
      title: "Performance Metrics",
      description: "Track program effectiveness, participant outcomes, and ROI with customizable dashboards.",
    },
  ];

  const benefits = [
    "Volume pricing for state and local programs",
    "WCAG 2.1 AA accessibility compliance",
    "Custom integrations with state systems",
    "Veteran and military spouse support",
    "Section 508 accessibility certified",
    "Flexible procurement options",
    "Dedicated government support team",
    "Training for case managers and staff",
  ];

  return (
    <>
      <Helmet>
        <title>Government Workforce Solutions | LoopBaan - Public Sector Employment Services</title>
        <meta
          name="description"
          content="Help citizens find meaningful employment with LoopBaan's government workforce platform. FISMA compliant, grant reporting, multi-language support, and proven results."
        />
      </Helmet>

      <MegaMenuNav />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
              <Landmark className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">Government Agencies</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Empower Citizens
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                With Modern Career Tools
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Help unemployed and underemployed citizens find meaningful work faster with AI-powered tools designed for government workforce programs.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Request Government Proposal
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Public Sector Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Secure</div>
              <div className="text-gray-400 text-sm md:text-base">Government-Grade</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Multi</div>
              <div className="text-gray-400 text-sm md:text-base">Language Support</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Grant</div>
              <div className="text-gray-400 text-sm md:text-base">Reporting Tools</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">FISMA</div>
              <div className="text-gray-400 text-sm md:text-base">Compliant</div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Built For Government Workforce Programs
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Secure, compliant, and effective solutions for public sector employment services
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700 p-8 hover:border-teal-500/50 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-500/10 rounded-lg">
                    <feature.icon className="h-8 w-8 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Why Government Agencies Choose LoopBaan
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4">
                  <CheckCircle2 className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 rounded-2xl p-12 text-center">
            <Landmark className="h-16 w-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Modernize Workforce Services?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how LoopBaan can help your agency serve citizens more effectively.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                Contact Government Team
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
